import type { AnalyticsRow } from "../pages/analytics.js";
import type { DashboardSnapshot } from "../api/client.js";
import {
  PROTOTYPE_GAMES,
  PROTOTYPE_HEALTH,
  PROTOTYPE_LEDGER,
  PROTOTYPE_LINEUPS,
  PROTOTYPE_RECOMMENDATIONS,
} from "./scenario.js";
import type {
  PrototypeActionResult,
  PrototypeLedgerRow,
  PrototypeLineupDecision,
  PrototypePlacementInput,
  PrototypeSession,
} from "./types.js";

const AU_BOOKMAKERS = new Set([
  "sportsbet",
  "bet365",
  "tab",
  "pointsbet",
  "ladbrokes",
  "dabble",
  "picklebet",
  "betr",
  "nextbet",
]);

const success = (session: PrototypeSession): PrototypeActionResult => ({
  ok: true,
  code: "OK",
  session,
});

const failure = (
  session: PrototypeSession,
  code: Exclude<PrototypeActionResult["code"], "OK">,
): PrototypeActionResult => ({ ok: false, code, session });

export function createPrototypeSession(): PrototypeSession {
  return {
    games: PROTOTYPE_GAMES.map((game) => ({ ...game })),
    bankrollCents: 1_000_000,
    unitCents: 2_000,
    committedCents: PROTOTYPE_LEDGER.filter(
      (row) => row.status === "PLACED",
    ).reduce((sum, row) => sum + row.stakeCents, 0),
    recommendations: PROTOTYPE_RECOMMENDATIONS.map((recommendation) => ({
      ...recommendation,
      evidence: [...recommendation.evidence],
      offers: recommendation.offers.map((offer) => ({ ...offer })),
    })),
    lineups: PROTOTYPE_LINEUPS.map((lineup) => ({
      ...lineup,
      recommendation: {
        ...lineup.recommendation,
        evidence: [...lineup.recommendation.evidence],
        offers: lineup.recommendation.offers.map((offer) => ({ ...offer })),
      },
    })),
    ledger: PROTOTYPE_LEDGER.map((row) => ({ ...row })),
    health: {
      ...PROTOTYPE_HEALTH,
      components: PROTOTYPE_HEALTH.components.map((component) => ({
        ...component,
      })),
    },
  };
}

export function prototypeDashboardSnapshot(
  session: PrototypeSession,
): DashboardSnapshot {
  const actionable = session.recommendations.filter(
    (recommendation) => recommendation.status === "RECOMMENDED",
  );
  return {
    bankrollCents: session.bankrollCents,
    unitCents: session.unitCents,
    committedCents: session.committedCents,
    recommendedCents: actionable.reduce(
      (sum, recommendation) => sum + recommendation.stakeCents,
      0,
    ),
    replayReady: session.health.replayReady,
    recommendations: session.recommendations,
  };
}

export function confirmPrototypeLineup(
  session: PrototypeSession,
  input: {
    candidateId: string;
    status: PrototypeLineupDecision;
  },
): PrototypeActionResult {
  const candidate = session.lineups.find(
    (lineup) => lineup.candidateId === input.candidateId,
  );
  if (candidate === undefined) return failure(session, "NOT_FOUND");

  const remainingLineups = session.lineups.filter(
    (lineup) => lineup.candidateId !== input.candidateId,
  );
  if (input.status === "CONFIRMED_STARTER") {
    return success({
      ...session,
      lineups: remainingLineups,
      recommendations: session.recommendations.filter(
        (recommendation) =>
          recommendation.signalId !== candidate.recommendation.signalId,
      ),
    });
  }

  return success({
    ...session,
    lineups: remainingLineups,
    recommendations: session.recommendations.map((recommendation) =>
      recommendation.signalId === candidate.recommendation.signalId
        ? { ...recommendation, status: "RECOMMENDED" as const }
        : recommendation,
    ),
  });
}

export function placePrototypeBet(
  session: PrototypeSession,
  input: PrototypePlacementInput,
): PrototypeActionResult {
  if (input.bookmaker === "draftkings")
    return failure(session, "REFERENCE_ONLY");
  if (!AU_BOOKMAKERS.has(input.bookmaker)) return failure(session, "NOT_FOUND");
  if (session.ledger.some((row) => row.signalId === input.signalId))
    return failure(session, "ALREADY_PLACED");

  const recommendation = session.recommendations.find(
    (row) => row.signalId === input.signalId && row.status === "RECOMMENDED",
  );
  if (recommendation === undefined) return failure(session, "NOT_FOUND");
  if (input.actualPrice < recommendation.minimumOdds)
    return failure(session, "BELOW_MINIMUM_ODDS");
  if (input.stakeCents > recommendation.stakeCents || input.stakeCents <= 0)
    return failure(session, "STAKE_EXCEEDS_ALLOCATION");

  const slateCap = Math.min(
    30 * session.unitCents,
    Math.floor(session.bankrollCents * 0.1),
  );
  if (session.committedCents + input.stakeCents > slateCap)
    return failure(session, "RISK_CAP_EXCEEDED");

  const ledgerRow: PrototypeLedgerRow = {
    betId: `prototype-placement-${input.signalId}`,
    signalId: input.signalId,
    placedAt: "2026-10-28T03:05:00.000Z",
    ruleVersion: recommendation.ruleVersion,
    description: recommendation.description,
    gameLabel: recommendation.gameLabel,
    bookmaker: input.bookmaker,
    actualLine: input.actualLine,
    actualPrice: input.actualPrice,
    stakeCents: input.stakeCents,
    units: input.stakeCents / session.unitCents,
    status: "PLACED",
    result: null,
    profitUnits: null,
    closingLine: null,
    closingPrice: null,
    lineClv: null,
    priceClv: null,
    synthetic: true,
  };

  return success({
    ...session,
    committedCents: session.committedCents + input.stakeCents,
    recommendations: session.recommendations.filter(
      (row) => row.signalId !== input.signalId,
    ),
    ledger: [ledgerRow, ...session.ledger],
  });
}

export function settlePrototypeBet(
  session: PrototypeSession,
  betId: string,
): PrototypeActionResult {
  const row = session.ledger.find((item) => item.betId === betId);
  if (row === undefined || row.status !== "PLACED")
    return failure(session, "NOT_FOUND");

  const newPlacement = row.signalId === "prototype-signal-3pm";
  const settled: PrototypeLedgerRow = {
    ...row,
    status: "SETTLED",
    result: newPlacement ? "WIN" : "LOSS",
    profitUnits: newPlacement ? row.units * (row.actualPrice - 1) : -row.units,
    closingLine: newPlacement ? row.actualLine : 3.5,
    closingPrice: newPlacement ? 1.86 : 1.84,
    lineClv: newPlacement ? 0 : -1,
    priceClv: newPlacement ? row.actualPrice - 1.86 : row.actualPrice - 1.84,
  };

  return success({
    ...session,
    ledger: session.ledger.map((item) =>
      item.betId === betId ? settled : item,
    ),
  });
}

const csvCell = (value: string | number | null): string => {
  const text = value === null ? "" : String(value);
  return `"${text.replaceAll('"', '""')}"`;
};

export function prototypeCsv(rows: readonly PrototypeLedgerRow[]): string {
  const headers = [
    "data_classification",
    "bet_id",
    "signal_id",
    "placed_at",
    "rule_version",
    "description",
    "game",
    "bookmaker",
    "actual_line",
    "actual_price",
    "stake_cents",
    "units",
    "status",
    "result",
    "profit_units",
    "closing_line",
    "closing_price",
    "line_clv",
    "price_clv",
  ];
  const records = rows.map((row) =>
    [
      "PROTOTYPE_SYNTHETIC_DATA",
      row.betId,
      row.signalId,
      row.placedAt,
      row.ruleVersion,
      row.description,
      row.gameLabel,
      row.bookmaker,
      row.actualLine,
      row.actualPrice,
      row.stakeCents,
      row.units,
      row.status,
      row.result,
      row.profitUnits,
      row.closingLine,
      row.closingPrice,
      row.lineClv,
      row.priceClv,
    ]
      .map(csvCell)
      .join(","),
  );
  return [headers.join(","), ...records].join("\n");
}

/**
 * Analytics rows derived from the prototype ledger.
 *
 * The analytics query is disabled in prototype mode - it reads a live view -
 * so the page rendered entirely empty and could not be judged at all. These
 * are computed from the same synthetic bets shown on the ledger, so the two
 * screens agree with each other rather than telling different stories.
 */
export function prototypeAnalytics(
  rows: readonly PrototypeLedgerRow[],
): AnalyticsRow[] {
  const settled = rows.filter((row) => row.result !== null);
  const graded = settled.filter(
    (row) => row.result === "WIN" || row.result === "LOSS",
  );

  const summarise = (
    scope: readonly PrototypeLedgerRow[],
    identity: Pick<
      AnalyticsRow,
      | "sport"
      | "rule_id"
      | "bookmaker"
      | "sport_scoped"
      | "rule_scoped"
      | "bookmaker_scoped"
    >,
  ): AnalyticsRow => {
    const scopeSettled = scope.filter((row) => row.result !== null);
    const scopeGraded = scopeSettled.filter(
      (row) => row.result === "WIN" || row.result === "LOSS",
    );
    const wins = scopeGraded.filter((row) => row.result === "WIN").length;
    const turnover = scope.reduce((total, row) => total + row.stakeCents, 0);
    const profit = scopeSettled.reduce(
      (total, row) => total + Math.round((row.profitUnits ?? 0) * 2000),
      0,
    );
    const clvRows = scopeSettled.filter((row) => row.priceClv !== null);
    const clvTotal = clvRows.reduce(
      (total, row) => total + (row.priceClv ?? 0),
      0,
    );
    return {
      ...identity,
      bets: scope.length,
      settled: scopeSettled.length,
      wins,
      losses: scopeGraded.length - wins,
      turnover_cents: turnover,
      profit_cents: profit,
      roi: turnover === 0 ? null : profit / turnover,
      strike_rate: scopeGraded.length === 0 ? null : wins / scopeGraded.length,
      avg_placed_price:
        scope.length === 0
          ? null
          : scope.reduce((total, row) => total + row.actualPrice, 0) /
            scope.length,
      avg_price_slippage: null,
      worse_than_quoted: 0,
      avg_price_clv: clvRows.length === 0 ? null : clvTotal / clvRows.length,
      price_clv_n: clvRows.length,
      beat_close: clvRows.filter((row) => (row.priceClv ?? 0) > 0).length,
      // Expected value from the closing price: what each bet was worth at the
      // moment it was struck, whatever it went on to return.
      expected_profit_cents: scopeSettled.reduce((total, row) => {
        if (row.closingPrice === null || row.closingPrice <= 1) return total;
        return (
          total +
          Math.round(row.stakeCents * (row.actualPrice / row.closingPrice - 1))
        );
      }, 0),
      expected_n: scopeSettled.filter((row) => row.closingPrice !== null)
        .length,
    };
  };

  const byKey = <T extends string>(
    key: (row: PrototypeLedgerRow) => T,
  ): Map<T, PrototypeLedgerRow[]> => {
    const groups = new Map<T, PrototypeLedgerRow[]>();
    for (const row of rows) {
      const value = key(row);
      groups.set(value, [...(groups.get(value) ?? []), row]);
    }
    return groups;
  };

  void graded;
  void settled;

  return [
    summarise(rows, {
      sport: "NBA",
      rule_id: "",
      bookmaker: "",
      sport_scoped: false,
      rule_scoped: false,
      bookmaker_scoped: false,
    }),
    ...[...byKey((row) => row.ruleVersion)].map(([rule, scope]) =>
      summarise(scope, {
        sport: "NBA",
        rule_id: rule,
        bookmaker: "",
        sport_scoped: true,
        rule_scoped: true,
        bookmaker_scoped: false,
      }),
    ),
    ...[...byKey((row) => row.bookmaker)].map(([book, scope]) =>
      summarise(scope, {
        sport: "NBA",
        rule_id: "",
        bookmaker: book,
        sport_scoped: true,
        rule_scoped: false,
        bookmaker_scoped: true,
      }),
    ),
  ];
}
